import { InputHTMLAttributes } from 'react';
import { FieldValues } from 'react-hook-form';
import { t } from 'utils/getTranslations';
import { isValidNumber } from '../rhf-validators/validators';
import TextInput, { TextInputProps } from './TextInput';

interface PercentageInputProps<T extends FieldValues> extends TextInputProps<T> {
    inputProps?: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>;
    allowNegative?: boolean;
    decimalPlaces?: number;
    maxWholeDigits?: number;
}

const PercentageInput = <T extends FieldValues>({
    allowNegative,
    decimalPlaces = 4,
    maxWholeDigits = 2,
    inputProps,
    validate,
    ...restProps
}: PercentageInputProps<T>) => {
    const validatePercentageInput = (str: string) => {
        // Lets add 0 to the begining if user starts printing with dot or comma
        if (str.length === 1 && (str === '.' || str === ',')) {
            return '0.';
        }

        // Lets add dot after whole digits if user forgets to use it
        if (!str.startsWith('-') && str.length === maxWholeDigits + 1 && !str.includes('.') && !str.includes(',')) {
            return str.substring(0, maxWholeDigits) + '.' + str.substring(maxWholeDigits);
        }
        if (str.startsWith('-') && str.length === maxWholeDigits + 2 && !str.includes('.') && !str.includes(',')) {
            return str.substring(0, maxWholeDigits + 1) + '.' + str.substring(maxWholeDigits + 1);
        }

        // Backend cannot handle comma value. Lets allow user to use comma but just change it to dot.
        return str.replace(/,/g, '.');
    };

    const transformEventBeforeChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ): React.ChangeEvent<HTMLInputElement> => {
        if (inputProps?.min !== undefined && Number(e.target.value) < Number(inputProps.min)) {
            e.target.value = validatePercentageInput(String(inputProps.min));
        }
        if (inputProps?.max !== undefined && Number(e.target.value) > Number(inputProps.max)) {
            e.target.value = validatePercentageInput(String(inputProps.max));
        }
        if (e?.target?.value) {
            e.target.value = validatePercentageInput(e.target.value);
        }
        return e;
    };

    return (
        <TextInput
            {...restProps}
            validate={{
                ...(typeof validate === 'function' ? { custom: validate } : validate),
                mustBeNumber: isValidNumber(t('validation.must-be-number')),
            }}
            pattern={`^${allowNegative ? '-?' : ''}([0-9]{0,${maxWholeDigits}})[.,]?([0-9]{0,${decimalPlaces}})?$`}
            inputProps={inputProps}
            transformEventBeforeChange={transformEventBeforeChange}
        />
    );
};

export default PercentageInput;
